/**
 * Name Formatter Utility
 *
 * Provides utilities for detecting and formatting names based on language/script.
 * Supports automatic Japanese name ordering (LastName FirstName).
 */

/**
 * Detect if a string contains Japanese characters
 *
 * Checks for:
 * - Hiragana (ひらがな): \u3040-\u309F
 * - Katakana (カタカナ): \u30A0-\u30FF
 * - Kanji (漢字): \u4E00-\u9FAF (CJK Unified Ideographs)
 *
 * @param text - The text to check
 * @returns true if the text contains any Japanese characters
 *
 * @example
 * containsJapanese('山田') // true
 * containsJapanese('やまだ') // true
 * containsJapanese('ヤマダ') // true
 * containsJapanese('John') // false
 */
export function containsJapanese(text: string): boolean {
  if (!text) return false;

  // Japanese character ranges:
  // - Hiragana: U+3040 to U+309F
  // - Katakana: U+30A0 to U+30FF
  // - Kanji: U+4E00 to U+9FAF
  const japaneseRegex = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/;

  return japaneseRegex.test(text);
}

/**
 * Format full name based on language detection
 *
 * Automatically detects Japanese characters and formats the name accordingly:
 * - Japanese names: lastName firstName (e.g., "山田 太郎")
 * - Other names: firstName lastName (e.g., "John Doe")
 *
 * This follows the cultural convention where Japanese names are written
 * with family name first, while Western names use given name first.
 *
 * @param firstName - The first/given name
 * @param lastName - The last/family name
 * @returns Formatted full name with proper ordering
 *
 * @example
 * formatFullName('太郎', '山田') // "山田 太郎" (Japanese: last first)
 * formatFullName('John', 'Doe') // "John Doe" (Western: first last)
 * formatFullName('John', '山田') // "山田 John" (Mixed: detected as Japanese)
 * formatFullName('太郎', '') // "太郎" (Only first name)
 * formatFullName('', '山田') // "山田" (Only last name)
 * formatFullName('', '') // "" (Empty)
 */
export function formatFullName(firstName?: string, lastName?: string): string {
  const first = (firstName || '').trim();
  const last = (lastName || '').trim();

  // Handle empty cases
  if (!first && !last) return '';
  if (!first) return last;
  if (!last) return first;

  // Check if either name contains Japanese characters
  const isJapanese = containsJapanese(first) || containsJapanese(last);

  // Japanese: LastName FirstName
  // Others: FirstName LastName
  return isJapanese ? `${last} ${first}` : `${first} ${last}`;
}
